import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ToastrService } from 'ngx-toastr';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  // Inject services
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  // Get the auth token from the service
  const authToken = authService.getToken();
  
  // Clone the request and add the authorization header if token exists
  let authRequest = request;
  
  if (authToken) {
    // Check if this is a file upload (multipart/form-data) or already has content-type
    const isFileUpload = request.url.includes('/upload') || 
                        (request.body instanceof FormData);
    
    const headers: any = {
      'Authorization': `Bearer ${authToken}`
    };
    
    // Only set Content-Type for non-file uploads
    if (!isFileUpload && !request.headers.has('Content-Type')) {
      headers['Content-Type'] = 'application/json';
    }
    
    authRequest = request.clone({
      setHeaders: headers
    });
  } else {
    // Even without token, ensure Content-Type is set for JSON requests (but not file uploads)
    const isFileUpload = request.url.includes('/upload') || 
                        (request.body instanceof FormData);
    
    if (!isFileUpload && 
        request.headers.get('Content-Type') === null && 
        (request.method === 'POST' || request.method === 'PUT' || request.method === 'PATCH')) {
      authRequest = request.clone({
        setHeaders: {
          'Content-Type': 'application/json'
        }
      });
    }
  }

  // Handle the request and catch any authentication errors
  return next(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // Do not run global auth handling for login/signIn requests – let the login component show the error
      const isLoginRequest = request.url.includes('/user/student/signIn') || request.url.includes('/admin/teacher/signIn');
      if (isLoginRequest) {
        return throwError(() => error);
      }

      const isFileDownload =
    request.responseType === 'blob' ||
    request.responseType === 'arraybuffer';
      console.log(error, 'intercept')

      // Handle authentication errors for other requests
      if (error.status === 401) {
        handleUnauthorizedError(authService, router, toastr);
      } else if (error.status === 403) {
        handleForbiddenError(toastr);
      } else if (error.status === 0 && !isFileDownload) {
        handleNetworkError(toastr);
      }

      return throwError(() => error);
    })
  );
};

// Helper functions
function handleUnauthorizedError(authService: AuthService, router: Router, toastr: ToastrService): void {
  // Check if we're on a public route - don't redirect to login
  const currentUrl = router.url;
  const publicRoutes = ['/home', '/research', '/consultancy', '/', '/user-type-selection'];
  
  // Check if this is a non-critical API call that shouldn't trigger logout
  const nonCriticalEndpoints = [
    '/user/razorpay/payout-profile-status',
    '/user/razorpay/validate-bank-account'
  ];
  
  // Get the current request URL from the router state
  const requestUrl = router.url;
  const isNonCriticalEndpoint = nonCriticalEndpoints.some(endpoint => 
    requestUrl.includes(endpoint)
  );
  
  const isPublicRoute = publicRoutes.some(route => 
    currentUrl === route || currentUrl.startsWith(route + '/') || currentUrl.startsWith(route + '?')
  );
  
  if (isPublicRoute || isNonCriticalEndpoint) {
    // On public routes or non-critical endpoints, just clear auth data but don't redirect
    if (isNonCriticalEndpoint) {
      console.log('Auth error on non-critical endpoint, not logging out user');
      return; // Don't logout for non-critical endpoints
    }
    authService.logout();
    console.log('Auth error on public route, cleared auth data but staying on page');
    return;
  }
  
  // Clear authentication data and redirect to login only for protected routes
  authService.logout();
  toastr.error('Your session has expired. Please login again.', 'Session Expired');
  router.navigate(['/login']);
}

function handleForbiddenError(toastr: ToastrService): void {
  toastr.error('You do not have permission to access this resource.', 'Access Denied');
}

function handleNetworkError(toastr: ToastrService): void {
  toastr.error('Unable to connect to server. Please check your internet connection.', 'Network Error');
} 